import React from 'react';
import styled from 'styled-components';
import { handLineAfter } from '../../handLine';
import CaseStudyTemplate, {
  CaseStudySection,
  CaseStudyVideo,
  StoryParagraph,
} from './CaseStudyTemplate';

const DONUT_C_HREF = 'https://www.a1k0n.net/2021/01/13/optimizing-donut.html';

const StorySubheading = styled.h3`
  margin: 1.35rem 0 0.65rem;
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  color: var(--text);
  letter-spacing: -0.02em;
  line-height: 1.35;

  &:first-child {
    margin-top: 0;
  }
`;

const CodeBlock = styled.pre`
  margin: 0 0 1rem;
  padding: 1rem 1.1rem;
  border-radius: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  overflow-x: auto;
  font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 16px;
  line-height: 1.5;
  color: var(--text);
  tab-size: 2;
`;

const TextLink = styled.a`
  position: relative;
  color: inherit;
  text-decoration: none;
  overflow-wrap: anywhere;

  &::after {
    ${handLineAfter}
  }

  &:hover {
    color: var(--text);
  }

  &:hover::after {
    opacity: 1;
    transform: scaleY(1.2);
  }
`;

/** Block spans — intro lives inside CaseStudyTemplate's <p>, so we avoid nested <p> */
const IntroLine = styled.span`
  display: block;
  margin: 0 0 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
    font-size: 0.92em;
  }
`;

const AsciiArtLoadingCaseStudy = () => (
  <CaseStudyTemplate
    title="Building an ASCII spiral tunnel loading page"
    intro={
      <>
        <IntroLine>
          An ASCII intro animation inspired by Andy Sloane&apos;s{' '}
          <TextLink href={DONUT_C_HREF} target="_blank" rel="noopener noreferrer">
            donut.c
          </TextLink>{' '}
          , the idea that you can render convincing 3D geometry using nothing but a character grid, some rotation math, and a depth palette. No canvas. No WebGL. Just a <code>pre</code> tag.
        </IntroLine>
      </>
    }
    details={[
      { label: 'Type', value: 'Side project, dev work for a friend' },
      { label: 'Stack', value: 'React, JavaScript, inline styles' },
      { label: 'Role', value: 'Developer, Design Engineer' },
    ]}
    backTarget="selected-work"
    afterIntro={
      <CaseStudyVideo
        src="/hannah.mp4"
        caption="Screen recording of the spiral tunnel, blackout, and transition into the terminal intro."
        ariaLabel="ASCII spiral tunnel animation demo"
        flushTop
      />
    }
  >
    <CaseStudySection id="effect" title="The effect">
      <StoryParagraph>
        On load, an ASCII tunnel spirals outward from the center of the screen — layer by layer, rotating and twisting as it expands — then crashes to black and hands off to a terminal intro. The whole sequence takes a few seconds and sets a deliberate tone before a single word of content appears.
      </StoryParagraph>
    </CaseStudySection>

    <CaseStudySection id="how-spiral" title="How the spiral works">
      <StorySubheading>1. Build a character grid from the viewport</StorySubheading>
      <StoryParagraph>
        The first problem: how do you map a pixel viewport to a text grid? I assumed fixed monospace cell dimensions (8×16px) and derived column and row counts from <code>window.innerWidth</code> and <code>window.innerHeight</code>. Font size scales proportionally so the grid fills the screen at any viewport size.
      </StoryParagraph>
      <CodeBlock>{`const charWidth = 8;
const charHeight = 16;
const cols = Math.floor(window.innerWidth / charWidth);
const rows = Math.floor(window.innerHeight / charHeight);
setFontSize(Math.min(window.innerWidth, window.innerHeight) / 60);`}</CodeBlock>
      <StoryParagraph>
        Each frame allocates a fresh 2D grid of spaces — no diffing, no virtual DOM tricks, just a full redraw at 50ms intervals.
      </StoryParagraph>

      <StorySubheading>2. Precompute the spiral, then reveal it frame by frame</StorySubheading>
      <StoryParagraph>
        The spiral is precomputed once on mount. I divide the space from center to max radius into 100 layers. Each layer gets a ring of points proportional to its circumference, each point stored with its <code>radius</code>, <code>baseAngle</code>, and <code>layer</code> index.
      </StoryParagraph>
      <CodeBlock>{`const maxRadius = Math.sqrt(cols * cols + rows * rows) * 0.9;
const layers = 100;
const layerSpacing = maxRadius / layers;

for (let i = 0; i < layers; i++) {
  const radius = maxRadius - i * layerSpacing;
  const numPoints = Math.floor(2 * Math.PI * radius * 0.5);
  for (let j = 0; j < numPoints; j++) {
    const baseAngle = (j / numPoints) * 2 * Math.PI;
    spiralPoints.push({ radius, baseAngle, layer: i });
  }
}`}</CodeBlock>
      <StoryParagraph>
        On each frame, a <code>currentLayer</code> counter increments. Only points whose <code>layer &gt;= currentLayer</code> are drawn — so the spiral peels inward from the outside, one ring at a time. Combined with a <code>frameCount * rotationSpeed</code> offset added to each angle, the whole structure rotates as it reveals. A per-layer <code>twistOffset</code> (<code>layer * 0.08</code>) adds the helical twist that makes it read as a tunnel rather than a flat disc.
      </StoryParagraph>
      <CodeBlock>{`const angle = p.baseAngle + frameCount * rotationSpeed + twistOffset;`}</CodeBlock>
      <StoryParagraph>
        This is the same conceptual move as donut.c: instead of tracking absolute angles, you accumulate rotation incrementally each frame. Sloane does it with a rotation matrix and a Newton renormalization step to prevent magnitude drift; here the drift doesn&apos;t accumulate dangerously because each frame is redrawn from the precomputed <code>baseAngle</code> plus an offset.
      </StoryParagraph>

      <StorySubheading>3. Vertical compression for monospace cells</StorySubheading>
      <StoryParagraph>
        Monospace characters are roughly twice as tall as they are wide. Without correction, circles become ovals and tunnels become tubes. The fix is one character: divide the <code>sin</code> component by 2.
      </StoryParagraph>
      <CodeBlock>{`const x = Math.floor(centerX + (p.radius + scatter) * Math.cos(angle));
const y = Math.floor(centerY + (p.radius + scatter) * Math.sin(angle) / 2);`}</CodeBlock>
      <StoryParagraph>
        Same trick used in donut.c and most ASCII art renderers — the vertical axis is compressed to compensate for cell aspect ratio.
      </StoryParagraph>

      <StorySubheading>4. Depth palette + film grain</StorySubheading>
      <StoryParagraph>
        Characters are assigned from a six-symbol palette based on layer depth —{' '}
        <code>{`['|', '=', '-', '.', '\`', "'"]`}</code> — so outer rings (closer to the viewer) use denser characters and inner rings fade to lighter ones. The effect reads as depth without any actual z-buffer.
      </StoryParagraph>
      <CodeBlock>{`const symbolIndex = Math.min(
  glowSymbols.length - 1,
  Math.floor((p.layer / layers) * glowSymbols.length)
);
grid[y][x] = glowSymbols[symbolIndex];`}</CodeBlock>
      <StoryParagraph>
        Film grain adds ~1% random noise across the grid each frame — cells get a <code>.</code> or <code>'</code> at random — which breaks up the regularity and makes the animation feel more alive.
      </StoryParagraph>

      <StorySubheading>5. Blackout and handoff</StorySubheading>
      <StoryParagraph>
        When <code>currentLayer</code> exceeds the layer count, a blackout overlay mounts above the <code>pre</code> (<code>zIndex: 10</code> vs <code>zIndex: 1</code>), holds for 1500ms, then fires <code>onComplete</code> — passing control to the terminal intro stage. The timing is a deliberate breath: abrupt enough to feel like a cut, long enough to register.
      </StoryParagraph>
    </CaseStudySection>

    <CaseStudySection id="state-machine" title="State machine context">
      <StoryParagraph>
        The spiral is one act in a three-stage intro: <code>spiral → terminal → homepage</code>. Each stage is its own React component with a single <code>onComplete</code> or <code>onEnter</code> prop. No shared state, no context — each component is responsible for its own lifecycle and signals completion upward. Clean handoffs, easy to swap or extend.
      </StoryParagraph>
    </CaseStudySection>

    <CaseStudySection id="retrospective" title={"Next steps"}>
      <StoryParagraph>
        This was a fun project to build and I'm proud of the results. This is supposed to be a portfolio showcasing my friend's work so I can't wait to create the project page with this ASCII art theme. 
      </StoryParagraph>
    </CaseStudySection>
  </CaseStudyTemplate>
);

export default AsciiArtLoadingCaseStudy;
